import React, { useMemo } from "react";
import {
  Calendar,
  RefreshCw,
  Timer,
  Pizza,
  Flame,
  Bike,
  Truck,
  ChefHat,
} from "lucide-react";
import { BrazilDate } from "../../../utils/BrazilDate";
import {
  useInspecaoTempoMedio,
  useInspecaoSnapshots,
} from "../../../hooks/useInspecao";
import { useInspecaoContext } from "../context/InspecaoContext";
import { InspecaoSnapshotsTable } from "./InspecaoSnapshotsTable";

export const InspecaoResumoSection: React.FC = () => {
  const {
    user,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    selectedLojaId,
    setSelectedLojaId,
    lojas,
  } = useInspecaoContext();

  const {
    data: tempos,
    isLoading,
    refetch,
    isFetching,
  } = useInspecaoTempoMedio({
    dataInicio,
    dataFim,
    lojaId: selectedLojaId === "all" ? undefined : (selectedLojaId as number),
  });

  const snapshotParams =
    dataInicio.trim() !== "" && dataFim.trim() !== ""
      ? {
          dataInicio,
          dataFim,
          lojaId: selectedLojaId === "all" ? null : (selectedLojaId as number),
        }
      : null;
  const { data: snapshots, isLoading: isLoadingSnapshots } =
    useInspecaoSnapshots(snapshotParams);

  const handleReload = () => {
    refetch();
  };

  const cards = useMemo(
    () => [
      {
        label: "Pizzas produzidas",
        value:
          tempos?.quantidadeProducoes != null
            ? String(tempos.quantidadeProducoes)
            : "0",
        icon: <Pizza size={16} />,
        subtitle: "Quantidade de produções no período",
      },
      {
        label: "Aguardando Montagem",
        value: tempos?.tempoMedioAguardandoMontagem ?? "00:00:00",
        icon: <ChefHat size={16} />,
      },
      {
        label: "Montagem",
        value: tempos?.tempoMedioMontagem ?? "00:00:00",
        icon: <ChefHat size={16} />,
      },
      {
        label: "Forno",
        value: tempos?.tempoMedioForno ?? "00:00:00",
        icon: <Flame size={16} />,
      },
      {
        label: "Aguardando Entrega",
        value: tempos?.tempoMedioAguardandoEntrega ?? "00:00:00",
        icon: <Truck size={16} />,
        subtitle: "Média na fila aguardando entrega (igual ao painel)",
      },
      {
        label: "Entrega (rota)",
        value: tempos?.tempoMedioEntrega ?? "00:00:00",
        icon: <Bike size={16} />,
        subtitle: "Média ponderada por pedido/endereço (igual ao painel)",
      },
      {
        label: "Tempo médio de produção",
        value: tempos?.tempoMedioTotalProducao ?? "00:00:00",
        highlight: true,
        icon: <Timer size={16} />,
        subtitle: "Aguardando montagem + Montagem + Forno",
      },
      {
        label: "Tempo médio de entrega (total)",
        value: tempos?.tempoMedioTotalEntrega ?? "00:00:00",
        highlight: true,
        icon: <Bike size={16} />,
        subtitle: "Aguardando entrega + em rota (soma das médias no período)",
      },
      {
        label: "Tempo total",
        value: tempos?.tempoMedioTotal ?? "00:00:00",
        highlight: true,
        icon: <Timer size={16} />,
        subtitle:
          "Montagem + Forno + Aguardando Entrega + Entrega + Aguardando Montagem",
      },
    ],
    [tempos]
  );

  const periodLabel =
    dataInicio === dataFim
      ? BrazilDate.formatBR(BrazilDate.fromISOString(dataInicio), false)
      : `${BrazilDate.formatBR(
          BrazilDate.fromISOString(dataInicio),
          false
        )} — ${BrazilDate.formatBR(BrazilDate.fromISOString(dataFim), false)}`;

  const lojaSelecionada =
    tempos?.lojaId != null
      ? (lojas.find((loja: any) => loja.id === tempos.lojaId)?.nome ??
        `Loja #${tempos.lojaId}`)
      : undefined;

  return (
    <>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-sm">
              <Calendar size={16} className="text-neutral-500" />
              <span className="text-xs font-medium text-neutral-500">
                Início
              </span>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="cursor-pointer border-none bg-transparent text-sm text-neutral-800 outline-none"
              />
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-sm">
              <Calendar size={16} className="text-neutral-500" />
              <span className="text-xs font-medium text-neutral-500">Fim</span>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="cursor-pointer border-none bg-transparent text-sm text-neutral-800 outline-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-sm">
            <span className="text-xs font-medium text-neutral-500">Loja</span>
            <select
              value={selectedLojaId}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedLojaId(
                  value === "all" ? "all" : Number.parseInt(value, 10)
                );
              }}
              className="cursor-pointer border-none bg-transparent text-sm text-neutral-800 outline-none"
            >
              <option value="all">
                {user?.role?.nome?.toLowerCase().includes("admin")
                  ? "Todas"
                  : "Minha loja"}
              </option>
              {lojas.map((loja: any) => (
                <option key={loja.id} value={loja.id}>
                  {loja.nome}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleReload}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70"
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-lg bg-white p-4 shadow">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <span className="block text-sm font-medium text-neutral-800">
              Filtros aplicados
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700">
                <span className="text-[11px] uppercase tracking-wide text-neutral-500">
                  Período
                </span>
                <span>{periodLabel}</span>
              </span>
              {lojaSelecionada && (
                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700">
                  <span className="text-[11px] uppercase tracking-wide text-neutral-500">
                    Loja
                  </span>
                  <span>{lojaSelecionada}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-neutral-500">
            Carregando tempos médios...
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => (
              <div
                key={card.label}
                className={`rounded-lg border p-4 ${
                  card.highlight
                    ? "border-primary bg-primary/5"
                    : "border-neutral-200 bg-neutral-50/80"
                }`}
              >
                <div
                  className={`flex items-center gap-2 text-sm font-medium ${
                    card.highlight ? "text-primary" : "text-neutral-600"
                  }`}
                >
                  {card.icon}
                  {card.label}
                </div>
                <div className="mt-2 space-y-1">
                  <div
                    className={`text-2xl font-semibold tabular-nums ${
                      card.highlight ? "text-primary" : "text-neutral-900"
                    }`}
                  >
                    {card.value}
                  </div>
                  {"subtitle" in card && card.subtitle && (
                    <div className="text-xs text-neutral-500">
                      {card.subtitle}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InspecaoSnapshotsTable
        snapshots={snapshots}
        isLoading={isLoadingSnapshots}
      />
    </>
  );
};
